import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { CheckCircle2, ArrowRight, ArrowLeft, Loader2, Sparkles } from 'lucide-react';

const CATEGORY_OPTIONS = [
  { value: 'professional_training', label: 'Professional Training' },
  { value: 'tech_bootcamp', label: 'Tech Bootcamp' },
  { value: 'language', label: 'Language' },
  { value: 'creative', label: 'Creative' },
  { value: 'wellness', label: 'Wellness' },
];

const STEPS = ['Business Profile', 'Expertise', 'First Course', 'Subscription'];

export default function CourseProviderOnboardingWizard() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const [profile, setProfile] = useState({
    business_name: '', category: 'professional_training', website: '', email: '', phone: '',
    country: '', location: '', about: '',
  });
  const [expertise, setExpertise] = useState('');
  const [course, setCourse] = useState({ title: '', description: '', duration: '', price: '' });
  const [skipCourse, setSkipCourse] = useState(false);

  const next = () => setStep(s => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep(s => Math.max(s - 1, 0));

  const finishStep1 = () => {
    if (!profile.business_name.trim()) {
      toast.error('Business/trainer name is required');
      return;
    }
    next();
  };

  const finishStep2 = async () => {
    setSaving(true);
    try {
      await api.put('/course-providers/my-profile', {
        ...profile,
        expertise: expertise.split(',').map(s => s.trim()).filter(Boolean),
      });
      next();
    } catch {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const finishStep3 = async () => {
    if (!skipCourse && course.title.trim()) {
      setSaving(true);
      try {
        await api.post('/courses', {
          title: course.title,
          description: course.description || course.title,
          category: profile.category,
          duration: course.duration,
          price: course.price ? parseFloat(course.price) : 0,
          is_free: !course.price,
          institution_name: profile.business_name,
        });
        toast.success('First course created');
      } catch {
        toast.error('Failed to create course — you can add it later from your dashboard');
      } finally {
        setSaving(false);
      }
    }
    next();
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center p-4">
      <div className="w-full max-w-xl">
        <div className="flex items-center justify-center gap-2 mb-6">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${i <= step ? 'bg-purple-700 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && <div className={`w-8 h-0.5 ${i < step ? 'bg-purple-700' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Sparkles className="w-5 h-5 text-purple-700" />
              <CardTitle>{STEPS[step]}</CardTitle>
            </div>
            <CardDescription>Step {step + 1} of {STEPS.length} — set up your training business</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {step === 0 && (
              <>
                <div className="space-y-2">
                  <Label>Business/Trainer Name *</Label>
                  <Input value={profile.business_name} onChange={(e) => setProfile({ ...profile, business_name: e.target.value })} placeholder="Your training business name" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Website</Label>
                    <Input value={profile.website} onChange={(e) => setProfile({ ...profile, website: e.target.value })} placeholder="https://..." />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="+44 ..." />
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Input value={profile.country} onChange={(e) => setProfile({ ...profile, country: e.target.value })} placeholder="United Kingdom" />
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input value={profile.location} onChange={(e) => setProfile({ ...profile, location: e.target.value })} placeholder="City" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>About</Label>
                  <Textarea rows={4} value={profile.about} onChange={(e) => setProfile({ ...profile, about: e.target.value })} placeholder="Tell learners about your training business..." />
                </div>
                <div className="flex justify-end pt-2">
                  <Button onClick={finishStep1} className="bg-purple-700 hover:bg-purple-800">
                    Next<ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <select
                    value={profile.category}
                    onChange={(e) => setProfile({ ...profile, category: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500/20"
                  >
                    {CATEGORY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Areas of Expertise (comma-separated)</Label>
                  <Input value={expertise} onChange={(e) => setExpertise(e.target.value)} placeholder="Leadership, Excel, Public Speaking" />
                </div>
                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={back}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                  <Button onClick={finishStep2} disabled={saving} className="bg-purple-700 hover:bg-purple-800">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Next<ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <p className="text-sm text-muted-foreground">Add your first course now, or skip and do it later from your dashboard.</p>
                <div className="space-y-2">
                  <Label>Course Title</Label>
                  <Input value={course.title} onChange={(e) => setCourse({ ...course, title: e.target.value })} placeholder="e.g. Intro to Public Speaking" />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea rows={3} value={course.description} onChange={(e) => setCourse({ ...course, description: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Duration</Label>
                    <Input value={course.duration} onChange={(e) => setCourse({ ...course, duration: e.target.value })} placeholder="e.g. 2 weeks" />
                  </div>
                  <div className="space-y-2">
                    <Label>Price (£)</Label>
                    <Input type="number" value={course.price} onChange={(e) => setCourse({ ...course, price: e.target.value })} placeholder="Leave blank if free" />
                  </div>
                </div>
                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={back}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                  <div className="flex gap-2">
                    <Button variant="ghost" onClick={() => { setSkipCourse(true); next(); }}>Skip for now</Button>
                    <Button onClick={finishStep3} disabled={saving} className="bg-purple-700 hover:bg-purple-800">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Next<ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div className="text-center py-4">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-lg mb-1">Your profile is ready!</h3>
                  <p className="text-muted-foreground text-sm">
                    Choose a Course Provider plan to publish courses and reach learners. Pricing starts free — no card required for the Starter tier.
                  </p>
                </div>
                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={back}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                  <Button onClick={() => navigate('/pricing')} className="bg-purple-700 hover:bg-purple-800">
                    Choose Subscription<ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <div className="text-center">
                  <button onClick={() => navigate('/course-provider/dashboard')} className="text-sm text-muted-foreground hover:text-foreground underline mt-2">
                    Skip, go to dashboard
                  </button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
